import React, { createContext, useContext, useEffect, useState } from 'react';

import PropTypes from 'prop-types';

const QUALIFICATION_BASIS_DATA_TYPES = ['berufsbezeichnung', 'fachrichtung', 'zusatz'];

export const QUALIFICATION_ID_FIELDS = [
  ['bBerufbezStromId', 'b_berufbez_strom_id'],
  ['bBerufbezGasId', 'b_berufbez_gas_id'],
  ['bFachrichtungStromId', 'b_fachrichtung_strom_id'],
  ['bFachrichtungGasId', 'b_fachrichtung_gas_id'],
  ['bZusatzStromId', 'b_zusatz_strom_id'],
  ['bZusatzGasId', 'b_zusatz_gas_id'],
];

const EMPTY_BASIS_DATA = { berufsbezeichnung: [], fachrichtung: [], zusatz: [] };
const QualificationBasisDataContext = createContext(EMPTY_BASIS_DATA);

const MEISTER = { berufsbezeichnung: 'Meister', fachrichtung: null, zusatz: null };
const TREI = 'TREI-Zertifikat liegt vor';
const TRGI = 'TRGI-Zertifikat liegt vor';
const ANHANG = 'Anhang zum Zeugnis liegt vor';
const SICHERHEIT = 'Sicherheitsschein liegt vor';

const FIRST_QUALIFICATION_MAPPING = {
  masterTo1997Energy: MEISTER,
  masterFrom1998To2003Energy: MEISTER,
  masterFrom2004Energy: MEISTER,
  masterTo1997Gas: MEISTER,
  masterFrom1998To2003Gas: MEISTER,
  masterFrom2004Gas: MEISTER,
  otherQualificationEnergy: { berufsbezeichnung: null, fachrichtung: null, zusatz: TREI },
  technicalHighSchool: { berufsbezeichnung: null, fachrichtung: null, zusatz: TRGI },
  engineer: { berufsbezeichnung: null, fachrichtung: null, zusatz: TRGI },
  otherQualificationGas: { berufsbezeichnung: null, fachrichtung: null, zusatz: TRGI },
};

const SECOND_QUALIFICATION_MAPPING = {
  masterTo1997Electrician: { fachrichtung: 'Elektroinstallation', zusatz: null },
  masterTo1997OtherElectrical: { fachrichtung: null, zusatz: TREI },
  electrician1975: { fachrichtung: 'Elektroinstallation', zusatz: ANHANG },
  electromechanic1976: { fachrichtung: 'Elektromechanik', zusatz: TREI },
  telecomElectronics1994: { fachrichtung: 'Fernmeldeanlagenelektronik', zusatz: TREI },
  masterFrom1998To2003Electrician: { fachrichtung: 'Elektrotechnik', zusatz: ANHANG },
  masterFrom1998To2003OtherElectrical: { fachrichtung: null, zusatz: TREI },
  masterFrom2004Safety50PlusEnergy: { fachrichtung: 'Elektrotechnik', zusatz: SICHERHEIT },
  masterFrom2004Safety50MinusEnergy: { fachrichtung: 'Elektrotechnik', zusatz: TREI },
  masterTo1997Gas: { fachrichtung: 'Gas- und Wasserinstallation', zusatz: null },
  masterTo1997Heating: { fachrichtung: 'Zentralheizungs- u. Lüftungsbau', zusatz: TRGI },
  masterFrom1998To2003GasWater: { fachrichtung: 'Gas- und Wasserinstallation', zusatz: ANHANG },
  masterFrom1998To2003HeatingVentilation: {
    fachrichtung: 'Zentralheizungs- u. Lüftungsbau',
    zusatz: TRGI,
  },
  masterFrom2004Safety50Plus: { fachrichtung: 'Installateur- u. Heizungsbau', zusatz: SICHERHEIT },
  masterFrom2004Safety50Minus: { fachrichtung: 'Installateur- u. Heizungsbau', zusatz: TRGI },
  technicalSchoolSanitary: { berufsbezeichnung: null, fachrichtung: null, zusatz: TRGI },
  technicalSchoolClimate: { berufsbezeichnung: null, fachrichtung: null, zusatz: TRGI },
  engineerDegree: FIRST_QUALIFICATION_MAPPING.engineer,
  otherQualificationGas: FIRST_QUALIFICATION_MAPPING.otherQualificationGas,
  otherQualificationEnergy: FIRST_QUALIFICATION_MAPPING.otherQualificationEnergy,
};

const getQualificationBasisDataIds = (firstQualification, secondQualification, basisData) => {
  const firstMapping =
    typeof firstQualification === 'string'
      ? FIRST_QUALIFICATION_MAPPING[firstQualification]
      : undefined;
  const secondMapping =
    typeof secondQualification === 'string'
      ? SECOND_QUALIFICATION_MAPPING[secondQualification]
      : undefined;
  return Object.fromEntries(
    Object.entries({ ...firstMapping, ...secondMapping }).map(([type, label]) => {
      const entries = basisData[type].filter(
        (entry) => entry.label === label && Number(entry.id) > 0
      );
      if (label !== null && entries.length !== 1) {
        throw new Error(`EIV-Basisdaten fehlen oder sind nicht eindeutig: ${type}/${label}`);
      }
      return [type, entries[0]?.id ?? 0];
    })
  );
};

const toId = (value) => (value === undefined || value === null || value === '' ? 0 : value);

export const mapExpertToFachkraftPayload = (expert, basisData) => {
  const payload = {
    b_berufbez_strom_id: toId(expert.bBerufbezStromId),
    b_berufbez_gas_id: toId(expert.bBerufbezGasId),
    b_fachrichtung_strom_id: toId(expert.bFachrichtungStromId),
    b_fachrichtung_gas_id: toId(expert.bFachrichtungGasId),
    b_zusatz_strom_id: toId(expert.bZusatzStromId),
    b_zusatz_gas_id: toId(expert.bZusatzGasId),
  };
  const setQualificationIds = (first, second, branch) => {
    Object.entries(getQualificationBasisDataIds(first, second, basisData)).forEach(([type, id]) => {
      const prefix = type === 'berufsbezeichnung' ? 'berufbez' : type;
      Object.assign(payload, { [`b_${prefix}_${branch}_id`]: id });
    });
  };
  if (expert.isExpertEnergyActive !== false) {
    setQualificationIds(expert.firstQualificationEnergy, expert.secondQualificationEnergy, 'strom');
  }
  if (expert.isExpertGasActive !== false) {
    setQualificationIds(expert.firstQualificationGas, expert.secondQualificationGas, 'gas');
  }
  return payload;
};

const normalizeResponse = (response) =>
  (Array.isArray(response) ? response : [])
    .filter(
      (entry) =>
        entry &&
        typeof entry === 'object' &&
        entry.geloescht !== true &&
        (typeof entry.id === 'string' || typeof entry.id === 'number') &&
        typeof entry.name === 'string'
    )
    .map((entry) => ({ id: entry.id, label: entry.name }));

export const useQualificationBasisData = () => useContext(QualificationBasisDataContext);

export const QualificationBasisDataProvider = ({ children, enabled }) => {
  const [basisData, setBasisData] = useState(EMPTY_BASIS_DATA);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    let cancelled = false;
    const backendBase = (process.env.REACT_APP_GRAPHQL_ENDPOINT ?? '').replace(/\/graphql\/?$/, '');
    QUALIFICATION_BASIS_DATA_TYPES.forEach((type) => {
      fetch(`${backendBase}/api/external/basisdaten/${type}`)
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(`Basisdaten konnten nicht geladen werden: ${type}`);
          }
          const entries = normalizeResponse(await response.json());
          if (!cancelled) {
            setBasisData((current) => ({ ...current, [type]: entries }));
          }
        })
        .catch((error) => {
          console.error(error);
          if (!cancelled) {
            setHasError(true);
          }
        });
    });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return (
    <QualificationBasisDataContext.Provider value={basisData}>
      {hasError && <div role="alert">Qualifikationen konnten nicht geladen werden.</div>}
      {children}
    </QualificationBasisDataContext.Provider>
  );
};

QualificationBasisDataProvider.propTypes = {
  children: PropTypes.node.isRequired,
  enabled: PropTypes.bool,
};
QualificationBasisDataProvider.defaultProps = { enabled: true };
